import {
  getGameformatsFromGametypes,
  getAllNestedGameformats,
  getGameformatGroup,
} from '@/store/gameformat/gameformat'
import { toggleArr, SNK } from '@/helpers/helpers'
import { getRouteNameByStudyTab } from '@/store/router/router'
import router from '@/router'
import { namespaces } from '@/helpers/constants'

const getInitialState = () => ({
  gameformats: [],
  gameformats_favorite_ids: [],
  gameformats_nested_study: {},
  gameformats_nested_trainer: {},
  gameformats_groupsSet: {
    Cash: false,
    MTT: false,
    Spins: false,
    HuSng: false,
  },
})

const state = getInitialState()

const actions = {
  gameformat_set_legacy(
    { state, commit, dispatch, getters },
    { showLegacySolutions = true, callback } = {},
  ) {
    const current = getters.showLegacySolutions
    dispatch('userSettings', { showLegacySolutions })
    if (showLegacySolutions !== current) {
      setTimeout(() => {
        // delay inevitable lag - cos this is compute heavy
        // and usually user changes this only once and then he moves out so he might not notice this lag
        dispatch('gameformats_setNestedGmfs', {
          gameformats: getters.gameformats,
        })
        dispatch('gmfs_set_gameformat', {
          force_recomputeBase: true,
        })
        dispatch('tra/alpha/gmfs_set_gameformat', {
          force_recomputeBase: true,
        })
      }, 10)
    }
  },
  gameformat_set_gameformat({ dispatch, getters }, result) {
    if (result?.status !== 200) {
      console.error('Failed to fetch game formats')
      dispatch('messages', {
        type: 'error',
        status: 'error_gameformat_set_gameformat',
        statusMessages: {
          _error_gameformat_set_gameformat: {
            i18n: `messages.error_gameformat_set_gameformat`,
            i18nParams: { status: result?.status || 'unknown' },
          },
        },
      })
      return
    }

    dispatch('gameformat_set_gameformats_from_gametypes', {
      groups: result.actionArgs.groups,
      gametypes: result.response.data,
    })

    const channelId = getters.bc_channels_ids.find(id => id.includes('floating_gmf'))
    if (channelId) {
      dispatch(
        'bc_postMessage',
        {
          channelId,
          data: {
            code: 'gameformat_set_gameformats_from_gametypes',
            value: {
              groups: result.actionArgs.groups,
              gametypes: JSON.parse(JSON.stringify(result.response.data)),
            },
          },
        },
        { root: true },
      )
    }
  },
  gameformat_set_gameformats_from_gametypes(
    { state, commit, dispatch, getters },
    { groups, gametypes },
  ) {
    if (groups) {
      groups.forEach(group => {
        state.gameformats_groupsSet[group] = true
      })
    }

    const dev_showHidden = getters.user_has_flag('GMF_SHOW_HIDDEN')
    const gameformats = getGameformatsFromGametypes({ api_gametypes: gametypes, dev_showHidden })
    commit('gameformat_set', {
      key: 'gameformats',
      value: [...gameformats, ...getters.gameformats],
    })

    dispatch('gameformats_setNestedGmfs', { gameformats })
  },
  gameformats_setNestedGmfs({ state, commit, getters }, { gameformats }) {
    // hide legacy for gmfs_selektor
    let allGmfs = getters.gameformats
    if (!getters.showLegacySolutions) {
      gameformats = gameformats.filter(i => !i.legacy)
      allGmfs = allGmfs.filter(i => !i.legacy)
    }

    const nestedGmfsStudy = getAllNestedGameformats({
      key: 'study',
      allGmfs,
      gameformats,
      previousNested: state.gameformats_nested_study,
    })
    const nestedGmfsTrainer = getAllNestedGameformats({
      key: 'trainer',
      allGmfs,
      gameformats,
      previousNested: state.gameformats_nested_trainer,
    })

    const ids = allGmfs.filter(i => i.is_favorite).map(i => i.id)
    commit('gameformat_set', {
      key: 'gameformats_favorite_ids',
      value: ids,
    })

    commit('gameformat_set', {
      key: 'gameformats_nested_study',
      value: nestedGmfsStudy,
    })
    commit('gameformat_set', {
      key: 'gameformats_nested_trainer',
      value: nestedGmfsTrainer,
    })
  },
  gameformat_set_gameformats({ state, commit, getters }, { gameformats = null } = {}) {
    // @TODO delete once removed from everywhere
    const userSettings = getters.api_get_data('api_account_settings') || {}
    const api_gametypes = userSettings?.gametypes || []

    const dev_showHidden = getters.user_has_flag('GMF_SHOW_HIDDEN')
    let gmfs = gameformats || getGameformatsFromGametypes({ api_gametypes, dev_showHidden })

    commit('gameformat_set', {
      key: 'gameformats',
      value: gmfs,
    })

    const ids = gmfs.filter(i => i.is_favorite).map(i => i.id)
    commit('gameformat_set', {
      key: 'gameformats_favorite_ids',
      value: ids,
    })

    // hide legacy for gmfs_selektor
    if (!getters.showLegacySolutions) {
      gmfs = gmfs.filter(i => !i.legacy)
    }
    commit('gameformat_set', {
      key: 'gameformats_nested_study',
      value: getAllNestedGameformats({
        key: 'study',
        allGmfs: gmfs,
        gameformats: gmfs,
      }),
    })
    commit('gameformat_set', {
      key: 'gameformats_nested_trainer',
      value: getAllNestedGameformats({
        key: 'trainer',
        allGmfs: gmfs,
        gameformats: gmfs,
      }),
    })
  },
  gameformats_set_favorite({ state, commit, dispatch, getters }, { gameformats }) {
    let ids = state.gameformats_favorite_ids
    gameformats.forEach(gmf => {
      ids = toggleArr({ arr: ids, item: gmf.id })
    })
    commit('gameformat_set', {
      key: 'gameformats_favorite_ids',
      value: ids,
    })

    // share this across windows
    dispatch(
      'bc_postMessage',
      {
        channelId: 'app',
        data: {
          code: 'gmf_favorite_ids',
          value: JSON.parse(JSON.stringify(ids)),
        },
      },
      { root: true },
    )

    const calls = gameformats.map(gmf =>
      dispatch('api_gameformat_favorite', { id: gmf.id, is_favorite: ids.includes(gmf.id) }),
    )
    // const calls = gameformats.map((gmf) => dispatch('api_gameformat_favorite', { id: gmf.id, is_favorite: !gmf.is_favorite }))
    Promise.all(calls).then(values => {
      // give time for that arrow to rerender
      setTimeout(() => {
        // 404 no such gamemode === no such favorite so this counts as ok (on BE there is only a single list of ids)
        const oks = values.filter(i => [201, 204].some(s => s === i.res?.response?.status))
        const fails = values.filter(
          i => oks.every(ok => ok.args.id !== i.args.id) && i.res?.error?.response?.status !== 404,
        )

        if (oks.length) {
          const okIds = oks.map(i => i.args.id)
          const gmfs = state.gameformats.map(gmf => {
            if (okIds.includes(gmf.id)) {
              const { is_favorite } = oks.find(o => o.args.id === gmf.id).args
              const r = {
                ...gmf,
                is_favorite,
              }
              return r
            }
            return gmf
          })
          commit('gameformat_set', {
            key: 'gameformats',
            value: gmfs,
          })
          dispatch(
            'bc_postMessage',
            {
              channelId: 'app',
              data: {
                code: 'gameformats_update',
                value: JSON.parse(JSON.stringify(gmfs)),
              },
            },
            { root: true },
          )
        }

        if (fails.length) {
          let statuses = fails.map(i => i.res?.error?.response?.status || 'unknown')
          statuses = [...new Set(statuses)]
          statuses.forEach(status => {
            dispatch('messages', {
              type: 'error',
              status: 'error_gmf_favourite',
              statusMessages: {
                _error_gmf_favourite: {
                  i18n: `messages.error_gmf_favourite`,
                  i18nParams: { status: status || 'unknown' },
                },
              },
            })
          })
        }
      }, 0)
    })
  },
  async gameformats_reinitialize_after_tier_update({ commit, getters }) {
    if (getters.gameformat) {
      commit('gameformat_reset')
      commit('gmfs_reset')
      commit('router_reset')
    }
  },
  async gameformats_init_pageViewStores(
    { state, dispatch, getters, rootGetters },
    { forceInit, initPage } = {},
  ) {
    const pathName = getRouteNameByStudyTab(router, rootGetters)

    if (
      pathName === 'StudySolutions' ||
      pathName === 'StudyReports' ||
      pathName === 'DetachedGmfSelector'
    ) {
      const primaryPathName = pathName
      const secondaryPathName = pathName === 'StudySolutions' ? 'StudyReports' : 'StudySolutions'
      const groupPrimary = getGameformatGroup(
        router.currentRoute.value.query?.gametype ||
          JSON.parse(localStorage.getItem(primaryPathName))?.gametype,
        'Cash',
      )
      const groupSecondary = getGameformatGroup(
        JSON.parse(localStorage.getItem(secondaryPathName))?.gametype,
        'Cash',
      )

      const notInitedGroups = [...new Set([groupPrimary, groupSecondary])].filter(
        group => !state.gameformats_groupsSet[group],
      )
      if (notInitedGroups.length) {
        await dispatch('get_gameformat_groups', notInitedGroups)
      }
      if (!rootGetters[SNK(`gameformat`, namespaces.STUDY)] || forceInit) {
        dispatch(SNK('router_initialize', namespaces.STUDY), {
          lsKey: 'StudySolutions',
          localOnly: primaryPathName !== 'StudySolutions',
        })
        dispatch('poker_copy_module', {
          from_namespace: namespaces.STUDY,
          to_namespace: namespaces.STUDY,
          queryGroup: 'router-to-gmf-selektor',
        })
      }
      if (!rootGetters[SNK(`gameformat`, namespaces.REPORTS)] || forceInit) {
        dispatch(SNK('router_initialize', namespaces.REPORTS), {
          lsKey: 'StudyReports',
          localOnly: primaryPathName !== 'StudyReports',
        })
        dispatch('poker_copy_module', {
          from_namespace: namespaces.REPORTS,
          to_namespace: namespaces.REPORTS,
          queryGroup: 'router-to-gmf-selektor',
        })
      }
      return true
    }

    const getSelectedGameType = lsPathName => {
      const gametype =
        router.currentRoute.value.query?.gametype ||
        JSON.parse(localStorage.getItem(lsPathName))?.gametype
      return getGameformatGroup(gametype, 'Cash')
    }
    if (
      pathName === 'StudySolutions' ||
      pathName === 'PracticeRangeBuilder' ||
      pathName === 'DetachedGmfSelector'
    ) {
      const group = getSelectedGameType(pathName)
      if (!state.gameformats_groupsSet[group]) {
        await dispatch('get_gameformat_groups', [group])
      }
      if (!getters.gameformat || forceInit) {
        dispatch('router_initialize', {
          lsKey: pathName,
        })
        dispatch('poker_copy_module', {
          from_namespace: '',
          to_namespace: '',
          queryGroup: 'router-to-gmf-selektor',
        })
      }
    } else if (pathName === 'PracticeTrainer' || initPage === 'PracticeTrainer') {
      const group = getSelectedGameType('tra/alpha')
      if (!state.gameformats_groupsSet[group]) {
        await dispatch('get_gameformat_groups', [group])
      }
      if (!getters['tra/aplha/gameformat'] || forceInit) {
        dispatch('tra/alpha/router_initialize', {
          lsKey: 'tra/alpha',
        })
        dispatch('poker_copy_module', {
          from_namespace: 'tra/alpha',
          to_namespace: 'tra/alpha',
          queryGroup: 'router-to-gmf-selektor',
        })
        dispatch('poker_copy_module', {
          from_namespace: 'tra/alpha',
          to_namespace: 'tra/replay',
          queryGroup: 'router',
        })
      }
    }

    return true
  },
}

const getters = {
  gameformat_get: state => key => state[key],
  gameformats: state => state.gameformats,
  gameformats_favorite_ids: state => state.gameformats_favorite_ids,
  gameformats_groupsSet: state => state.gameformats_groupsSet,
}

const mutations = {
  gameformat_reset(state, initialState) {
    Object.assign(state, initialState || getInitialState())
  },
  gameformat_set(state, { key, value }) {
    state[key] = value
  },
}

export default {
  state,
  getters,
  actions,
  mutations,
}
